import { format, isBefore, parseISO } from "date-fns";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import * as Yup from "yup";

import {
  CHOCO_CHILL_MINT_FLAVOR,
  CITRUS_ZEN_FLAVOR,
  CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY,
  CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_SCOPOLAMINE_ADDON,
  CONSULTATION_TYPE_INSOMNIA,
  CONSULTATION_TYPE_INSOMNIA_FOLLOW_UP,
  CONSULTATION_TYPE_PERFORMANCE_ANXIETY,
  CONSULTATION_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP,
  DOCTOR_VISIT_STATUS_CLOSED_COMPLETE,
  FILL_PHARMACY_ID_HONEYBEE_CA,
  FILL_REQUEST_STATUS_CANCELLED,
  FILL_REQUEST_STATUS_DELETED,
  FILL_REQUEST_STATUS_ERROR,
  FILL_REQUEST_STATUS_FILLED,
  FILL_REQUEST_STATUS_INACTIVE,
  FILL_REQUEST_STATUS_MATCH_ERROR,
  FILL_REQUEST_STATUS_MATCHING_PENDING,
  FILL_REQUEST_STATUS_NEW,
  FILL_REQUEST_STATUS_PAYMENT_PENDING,
  FILL_REQUEST_STATUS_PAYMENT_UNCOLLECTIBLE,
  FILL_REQUEST_STATUS_PROCESSING,
  FILL_REQUEST_STATUS_PROCESSING_DONE,
  FILL_REQUEST_STATUS_SENDING_ERROR,
  FILL_REQUEST_STATUS_SENT,
  FLAVOR_NAME_CHOCO_CHILL_MINT,
  FLAVOR_NAME_CITRUS_ZEN,
  FLAVOR_NAME_FRUIT_PUNCH,
  FLAVOR_NAME_SPEARMINT,
  FLAVOR_NAME_TANGERINE,
  FRUIT_PUNCH_FLAVOR,
  ORDER_TYPE_ATE_SCO_1_PACK,
  ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP,
  ORDER_TYPE_ATE_SCO_1_PACK_REFILL,
  ORDER_TYPE_ATE_SCO_2_PACK,
  ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP,
  ORDER_TYPE_ATE_SCO_2_PACK_REFILL,
  ORDER_TYPE_PROP_1_PACK,
  ORDER_TYPE_PROP_1_PACK_FLAVORED,
  ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP,
  ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL,
  ORDER_TYPE_PROP_1_PACK_FOLLOW_UP,
  ORDER_TYPE_PROP_1_PACK_REFILL,
  ORDER_TYPE_PROP_2_PACK,
  ORDER_TYPE_PROP_2_PACK_FLAVORED,
  ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP,
  ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL,
  ORDER_TYPE_PROP_2_PACK_FOLLOW_UP,
  ORDER_TYPE_PROP_2_PACK_REFILL,
  ORDER_TYPE_PROP_SAMPLE_PACK_ALL_FLAVORS,
  ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED,
  ORDER_TYPE_PROP_VISIT_ONLY,
  ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP,
  ORDER_TYPE_SCOPOLAMINE_ADDON_10_PACK_MAIL_ORDER,
  ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_MAIL_ORDER,
  ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_VISIT_ONLY,
  ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_FOLLOW_UP,
  ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY,
  ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_QUARTERLY,
  ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_SEMIANNUAL,
  ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_FOLLOW_UP,
  ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY,
  ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY,
  ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_SEMIANNUAL,
  ORDER_TYPE_SLEEP_RX_TRIAL_MAIL_ORDER_ONE_MONTH,
  ORDER_TYPE_SLEEP_RX_TRIAL_MAIL_ORDER_TWO_MONTHS,
  ORDER_TYPE_SLEEP_RX_TRIAL_VISIT_ONLY_ONE_MONTH,
  ORDER_TYPE_SLEEP_RX_TRIAL_VISIT_ONLY_TWO_MONTHS,
  SPEARMINT_FLAVOR,
  TANGERINE_FLAVOR,
  VISIT_TYPE_GENERALIZED_ANXIETY,
  VISIT_TYPE_GENERALIZED_ANXIETY_FOLLOW_UP,
  VISIT_TYPE_INSOMNIA,
  VISIT_TYPE_INSOMNIA_FOLLOW_UP,
  VISIT_TYPE_PERFORMANCE_ANXIETY,
  VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP,
} from "../sharedConstants";
// -------------------------------------------------------------------

import imgChocoChillMint from "~assets/images/flavors/choco-chill.png";
import imgCitrusZen from "~assets/images/flavors/citrus-zen.png";
import imgSamplePackAllFlavors from "~assets/images/flavors/sample-pack-all-flavors.png";
import imgSpearmint from "~assets/images/flavors/spearmint-flavor.png";
import imgTangerine from "~assets/images/flavors/tangerine-flavor.png";
import imgFruitPunch from "~assets/images/flavors/tropical-fruit-medley-flavor.png";
import imgOnePack from "~assets/images/visits/one-pack.png";
import imgSleep from "~assets/images/visits/sleep-bottles.jpg";
import imgTwoPack from "~assets/images/visits/two-pack.png";

export function isDevelopmentEnvironment() {
  return process.env.NODE_ENV === "development";
}

// isPharmacyPickupOrderType returns true if the orderType is a pharmacy pickup order type
export function isPharmacyPickupOrderType(orderType) {
  return orderType?.includes("visit-only");
}

export function isMailOrderOrderType(orderType) {
  return !isPharmacyPickupOrderType(orderType);
}

// isVisitOnlyBBOrderType
export function isVisitOnlyBBOrderType(orderType) {
  return (
    orderType == ORDER_TYPE_PROP_VISIT_ONLY ||
    orderType == ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP
  );
}

export function isMailOrderBBOrderType(orderType) {
  return (
    orderType == ORDER_TYPE_PROP_2_PACK ||
    orderType == ORDER_TYPE_PROP_2_PACK_FOLLOW_UP ||
    orderType == ORDER_TYPE_PROP_2_PACK_REFILL ||
    orderType == ORDER_TYPE_PROP_1_PACK ||
    orderType == ORDER_TYPE_PROP_1_PACK_FOLLOW_UP ||
    orderType == ORDER_TYPE_PROP_1_PACK_REFILL ||
    orderType == ORDER_TYPE_PROP_1_PACK_FLAVORED ||
    orderType == ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP ||
    orderType == ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL ||
    orderType == ORDER_TYPE_PROP_2_PACK_FLAVORED ||
    orderType == ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP ||
    orderType == ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL
  );
}

export function isFlavoredBBOrderType(orderType) {
  return (
    orderType == ORDER_TYPE_PROP_1_PACK_FLAVORED ||
    orderType == ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP ||
    orderType == ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL ||
    orderType == ORDER_TYPE_PROP_2_PACK_FLAVORED ||
    orderType == ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP ||
    orderType == ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL
  );
}

export function isBBOrderType(orderType) {
  return isVisitOnlyBBOrderType(orderType) || isMailOrderBBOrderType(orderType);
}

export function isScopolamineAddonOrderType(orderType) {
  return (
    orderType == ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_MAIL_ORDER ||
    orderType == ORDER_TYPE_SCOPOLAMINE_ADDON_10_PACK_MAIL_ORDER ||
    orderType == ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_VISIT_ONLY
  );
}

export function isAteScoCompoundedOrderType(orderType) {
  return (
    orderType == ORDER_TYPE_ATE_SCO_1_PACK ||
    orderType == ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP ||
    orderType == ORDER_TYPE_ATE_SCO_2_PACK ||
    orderType == ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP
  );
}

export function isPharmacyPickupSleepOrderType(orderType) {
  return (
    orderType == ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY ||
    orderType == ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY ||
    orderType == ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_SEMIANNUAL ||
    orderType == ORDER_TYPE_SLEEP_RX_TRIAL_VISIT_ONLY_ONE_MONTH ||
    orderType == ORDER_TYPE_SLEEP_RX_TRIAL_VISIT_ONLY_TWO_MONTHS ||
    orderType == ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_FOLLOW_UP
  );
}

export function isMailOrderSleepOrderType(orderType) {
  return (
    orderType == ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY ||
    orderType == ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_QUARTERLY ||
    orderType == ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_SEMIANNUAL ||
    orderType == ORDER_TYPE_SLEEP_RX_TRIAL_MAIL_ORDER_ONE_MONTH ||
    orderType == ORDER_TYPE_SLEEP_RX_TRIAL_MAIL_ORDER_TWO_MONTHS ||
    orderType == ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_FOLLOW_UP
  );
}

export function isMailOrderType(orderType) {
  return (
    isMailOrderBBOrderType(orderType) || isMailOrderSleepOrderType(orderType)
  );
}

export function isSleepOrderType(orderType) {
  return (
    isPharmacyPickupSleepOrderType(orderType) ||
    isMailOrderSleepOrderType(orderType)
  );
}

// isPharmacyPickupFromVisit returns true if the differentPharmacyRawCookie is not empty
export function isPharmacyPickupFromVisit(differentPharmacyRawCookie) {
  // console.log("rawCookie: ", differentPharmacyRawCookie);
  // console.log(differentPharmacyRawCookie != `""`);
  return differentPharmacyRawCookie && differentPharmacyRawCookie != `""`;
}

export function isVisitTypeInsomnia(visitType) {
  return (
    visitType == VISIT_TYPE_INSOMNIA ||
    visitType == VISIT_TYPE_INSOMNIA_FOLLOW_UP
  );
}

export function isVisitTypePerformanceAnxiety(visitType) {
  return (
    visitType == VISIT_TYPE_PERFORMANCE_ANXIETY ||
    visitType == VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP
  );
}

export function isVisitTypeGeneralizedAnxiety(visitType) {
  return (
    visitType == VISIT_TYPE_GENERALIZED_ANXIETY ||
    visitType == VISIT_TYPE_GENERALIZED_ANXIETY_FOLLOW_UP
  );
}

export function isConsultationTypePerformanceAnxietyInitial(consultationType) {
  return (
    consultationType == CONSULTATION_TYPE_PERFORMANCE_ANXIETY ||
    consultationType == CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY
  );
}
export function isConsultationTypePerformanceAnxietyFollowUp(consultationType) {
  return (
    consultationType == CONSULTATION_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP ||
    consultationType ==
      CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP
  );
}
export function isConsultationTypeInsomniaInitial(consultationType) {
  return (
    consultationType == CONSULTATION_TYPE_INSOMNIA ||
    consultationType == CONSULTATION_TYPE_ABBREVIATION_INSOMNIA
  );
}

export function isConsultationTypeInsomniaFollowUp(consultationType) {
  return [
    CONSULTATION_TYPE_INSOMNIA_FOLLOW_UP,
    CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP,
  ].includes(consultationType?.toLowerCase());
}

export function isConsultationTypeInsomnia(consultationType) {
  return (
    isConsultationTypeInsomniaInitial(consultationType) ||
    isConsultationTypeInsomniaFollowUp(consultationType)
  );
}

// -------------------------------------------------------------------

export function refillOrderTypeFor(orderType) {
  switch (orderType) {
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
      return ORDER_TYPE_PROP_2_PACK_REFILL;
    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
      return ORDER_TYPE_PROP_1_PACK_REFILL;
    case ORDER_TYPE_PROP_VISIT_ONLY:
      return ORDER_TYPE_PROP_VISIT_ONLY;
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
      return ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL;
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
      return ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL;
    case ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED:
      return ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED;
    case ORDER_TYPE_ATE_SCO_1_PACK:
    case ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP:
      return ORDER_TYPE_ATE_SCO_1_PACK_REFILL;
    case ORDER_TYPE_ATE_SCO_2_PACK:
    case ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP:
      return ORDER_TYPE_ATE_SCO_2_PACK_REFILL;
  }
  return "";
}

export function followUpOrderTypeForRefillType(orderType) {
  switch (orderType) {
    case ORDER_TYPE_PROP_2_PACK_REFILL:
      return ORDER_TYPE_PROP_2_PACK_FOLLOW_UP;
    case ORDER_TYPE_PROP_1_PACK_REFILL:
      return ORDER_TYPE_PROP_1_PACK_FOLLOW_UP;
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL:
      return ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP;
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL:
      return ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP;
    default:
      return orderType;
  }
}

// isOriginalOrFollowUpVisit returns true if the orderType on a doctorVisit is not a refill order
export function isOriginalOrFollowUpVisit(orderType) {
  switch (orderType) {
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_VISIT_ONLY:
    case ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
    case ORDER_TYPE_ATE_SCO_1_PACK:
    case ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP:
    case ORDER_TYPE_ATE_SCO_2_PACK:
    case ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP:
      return true;
  }
  return false;
}

export function getDrugQuantityForOrderType(orderType) {
  switch (orderType) {
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_2_PACK_REFILL:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
    case ORDER_TYPE_ATE_SCO_2_PACK:
    case ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP:
      return 48;

    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_1_PACK_REFILL:
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_VISIT_ONLY:
    case ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
    case ORDER_TYPE_ATE_SCO_1_PACK:
    case ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP:
      return 24;

    case ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_MAIL_ORDER:
    case ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_VISIT_ONLY:
      return 4;
    case ORDER_TYPE_SCOPOLAMINE_ADDON_10_PACK_MAIL_ORDER:
      return 10;

    case ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED:
      return 6;

    case ORDER_TYPE_PROP_SAMPLE_PACK_ALL_FLAVORS:
      return 4;
  }
}

export function getNumRefillsForOrderType(orderType) {
  switch (orderType) {
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
    case ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_MAIL_ORDER:
    case ORDER_TYPE_SCOPOLAMINE_ADDON_10_PACK_MAIL_ORDER:
    case ORDER_TYPE_ATE_SCO_1_PACK:
    case ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP:
    case ORDER_TYPE_ATE_SCO_2_PACK:
    case ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP:
      return 3;

    case ORDER_TYPE_PROP_VISIT_ONLY:
    case ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP:
      return 1;
  }

  return 1;
}

// isDoctorVisitComplete returns true if a doctor visit has status of Closed Complete ('CC')
export function isDoctorVisitComplete(status) {
  return status == DOCTOR_VISIT_STATUS_CLOSED_COMPLETE;
}

// hasRefillsRemaining returns true if a doctorPrescription has refills available
export function hasRefillsAvailable(refills, numRefillsFilled) {
  return refills - numRefillsFilled > 0;
}

export function numRefillsRemaining(refills, numRefillsFilled) {
  let numRemaining = refills - numRefillsFilled;
  return numRemaining < 0 ? 0 : numRemaining;
}

// displayFriendlyLongDate returns date in "October 15 2020" format
export function displayFriendlyLongDate(date) {
  return format(new Date(date), "MMMM dd yyyy");
}

export function displayFriendlyDate(date) {
  return format(new Date(date), "MMM d, yyyy");
}

// displayISOShortDate returns date in "1/2/21" format, and returns blank if date is empty
export function displayISOShortDate(date) {
  const shortDate = parseISO(date);

  return isBefore(shortDate, new Date(0, 0, 1))
    ? ""
    : format(shortDate, "M/d/yy");
}

// displayFriendlyPrescriptionExpirationLongDate adds 360 days to the original prescription visit date {
export function displayFriendlyPrescriptionExpirationLongDate(date) {
  var result = new Date(date);
  result.setDate(result.getDate() + 362);

  return format(result, "MMMM dd yyyy");
}

export function displayFriendlyShortenedOrderType(orderType, subcategory) {
  switch (orderType) {
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
      return "2 Pack";
    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
      return "1 Pack";
    case ORDER_TYPE_PROP_VISIT_ONLY:
    case ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP:
      return "Pharmacy Pick-Up";
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
    case ORDER_TYPE_ATE_SCO_2_PACK:
    case ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP:
      return `2 Pack (${getFlavorName(subcategory) || "Flavored"})`;
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
    case ORDER_TYPE_ATE_SCO_1_PACK:
    case ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP:
      return `1 Pack (${getFlavorName(subcategory) || "Flavored"})`;
    case ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED:
      return `Sample Pack (${getFlavorName(subcategory) || "Flavored"})`;
    case ORDER_TYPE_PROP_SAMPLE_PACK_ALL_FLAVORS:
      return "All Flavors Sample Pack";
  }
  return "";
}

export function getOrderTypeImageSrc(productSlug, subcategory) {
  switch (productSlug) {
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_2_PACK_REFILL:
      return imgTwoPack;

    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_1_PACK_REFILL:
    case ORDER_TYPE_PROP_VISIT_ONLY:
      return imgOnePack;

    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY:
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_QUARTERLY:
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_SEMIANNUAL:
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_FOLLOW_UP:
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY:
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY:
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_SEMIANNUAL:
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_FOLLOW_UP:
      return imgSleep;

    case ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP: {
      switch (subcategory) {
        case TANGERINE_FLAVOR:
          return imgTangerine;
        case SPEARMINT_FLAVOR:
          return imgSpearmint;
        case FRUIT_PUNCH_FLAVOR:
          return imgFruitPunch;
        case CHOCO_CHILL_MINT_FLAVOR:
          return imgChocoChillMint;
        case CITRUS_ZEN_FLAVOR:
          return imgCitrusZen;
        default:
          return imgSpearmint;
      }
    }

    case ORDER_TYPE_PROP_SAMPLE_PACK_ALL_FLAVORS:
      return imgSamplePackAllFlavors;

    case ORDER_TYPE_ATE_SCO_1_PACK:
    case ORDER_TYPE_ATE_SCO_1_PACK_REFILL:
    case ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP:
    case ORDER_TYPE_ATE_SCO_2_PACK:
    case ORDER_TYPE_ATE_SCO_2_PACK_REFILL:
    case ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP: {
      switch (subcategory) {
        case CHOCO_CHILL_MINT_FLAVOR:
          return imgChocoChillMint;
        case CITRUS_ZEN_FLAVOR:
          return imgCitrusZen;
        default:
          return imgOnePack;
      }
    }
  }

  return imgOnePack;
}

export function getVisitTypeImageSrc(visitType) {
  switch (visitType) {
    case VISIT_TYPE_PERFORMANCE_ANXIETY:
    case VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP:
      return imgOnePack;

    case VISIT_TYPE_INSOMNIA:
    case VISIT_TYPE_INSOMNIA_FOLLOW_UP:
      return imgSleep;
  }
  return imgOnePack;
}

export function getConsultationNameFromVisitType(visitType) {
  switch (visitType) {
    case VISIT_TYPE_PERFORMANCE_ANXIETY:
    case VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP:
      return "Performance Anxiety";

    case VISIT_TYPE_INSOMNIA:
    case VISIT_TYPE_INSOMNIA_FOLLOW_UP:
      return "Insomnia";

    case VISIT_TYPE_GENERALIZED_ANXIETY:
    case VISIT_TYPE_GENERALIZED_ANXIETY_FOLLOW_UP:
      return "Generalized Anxiety";
  }
}

export function getConsultationNameFromType(consultationType) {
  switch (consultationType) {
    // Performance Anxiety
    case CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY:
      return "Performance Anxiety";
    case CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP:
      return "Performance Anxiety Follow-Up";
    case CONSULTATION_TYPE_ABBREVIATION_SCOPOLAMINE_ADDON:
      return "Scopolamine Addon";
    // Insomnia
    case CONSULTATION_TYPE_ABBREVIATION_INSOMNIA:
      return "Insomnia";
    case CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP:
      return "Insomnia Follow-Up";
    case CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY:
      return "Generalized Anxiety";
    case CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP:
      return "Generalized Anxiety Follow-Up";
  }
}

export function latestVisitCompletedAtOrVisitStartedAt(visitDate, completedAt) {
  return completedAt > visitDate ? completedAt : visitDate;
}

export function mapFillRequestStatusToUserFriendlyStatus(status) {
  switch (status) {
    case FILL_REQUEST_STATUS_NEW:
      return "New";
    case FILL_REQUEST_STATUS_SENT:
      return "Sent";
    case FILL_REQUEST_STATUS_SENDING_ERROR:
      return "Send Error";
    case FILL_REQUEST_STATUS_FILLED:
      return "Filled";
    case FILL_REQUEST_STATUS_ERROR:
      return "Error";
    case FILL_REQUEST_STATUS_CANCELLED:
      return "Cancelled";
    case FILL_REQUEST_STATUS_PROCESSING:
      return "Processing";
    case FILL_REQUEST_STATUS_PROCESSING_DONE:
      return "Processing Done";
    case FILL_REQUEST_STATUS_DELETED:
      return "Deleted";
    case FILL_REQUEST_STATUS_INACTIVE:
      return "Inactive";
    case FILL_REQUEST_STATUS_MATCHING_PENDING:
      return "Pending RX";
    case FILL_REQUEST_STATUS_PAYMENT_PENDING:
      return "Pending Payment";
    case FILL_REQUEST_STATUS_MATCH_ERROR:
      return "RX Match Error";
    case FILL_REQUEST_STATUS_PAYMENT_UNCOLLECTIBLE:
      return "Payment Uncollectible";
  }

  return status;
}

export async function copyTextToClipboard(copyText) {
  navigator.permissions.query({ name: "clipboard-write" }).then((result) => {
    console.log("clipboard access state: ", result.state);
    if (result.state == "granted" || result.state == "prompt") {
      try {
        navigator.clipboard.writeText(copyText);
        console.log("Copied to clipboard: ", copyText);
        /* Resolved - text copied to clipboard successfully */
      } catch (err) {
        console.error("Failed to copy: ", err);
        /* Rejected - text failed to copy to the clipboard */
      }
    } else {
      alert(`Clipboard access denied: ${copyText}`);
    }
  });
}

export const getGoogleAPIKey = () => {
  return (
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    "AIzaSyB-Lywz9aSHxSHQF23lrJJMKG3I75H2DmE"
  );
};

export const getZerobounceAPIKey = () => {
  return (
    process.env.REACT_APP_ZEROBOUNCE_API_KEY ||
    "84ed5171801b4eb7b70fc3a41f2bbe33"
  );
};

export function formatPhoneNumber(phoneNumberString) {
  var cleaned = ("" + phoneNumberString).replace(/\D/g, "");
  var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return "(" + match[1] + ") " + match[2] + "-" + match[3];
  }
  return null;
}

export function useQueryParams() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export const supportedUSStates = [
  "AL",
  "CA",
  "CO",
  "CT",
  "FL",
  "GA",
  "HI",
  "IA",
  "IL",
  "KS",
  "KY",
  "MA",
  "MD",
  "ME",
  "MI",
  "MN",
  "MT",
  "NC",
  "ND",
  "NE",
  "NH",
  "NJ",
  "NY",
  "OH",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VA",
  "WA",
  "WI",
  "WY",
];

export const supportedUSStatesForCompoundedMeds = [
  // "AK",
  // "AR",
  // "AZ",
  "CA",
  "CO",
  "CT",
  "FL",
  "GA",
  "HI",
  "IA",
  "ID",
  "IL",
  "KY",
  "MA",
  "MD",
  "ME",
  "MI",
  "MN",
  "MT",
  "NC",
  "ND",
  "NE",
  "NH",
  "NJ",
  "NM",
  "NY",
  "OH",
  // "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VA",
  // "VT",
  "WA",
  "WI",
  // "WV",
  "WY",
  // "DC",
];

export const controlledDrugsByState = {
  gabapentin: ["AL", "KY", "MT", "ND", "TN", "VA", "UT", "WV"],
  cyclobenzaprine: ["MN"],
};

export const pdmpDrugsByState = {
  gabapentin: [
    "CT",
    "DC",
    "IN",
    "KS",
    "MA",
    "MN",
    "NC",
    "NE",
    "NJ",
    "OH",
    "OR",
    "WI",
    "WY",
  ],
  cyclobenzaprine: ["DC", "WY"],
};

export const isPDMPSleepMedication = (usState, medicationName) => {
  return (pdmpDrugsByState[medicationName] || []).includes(usState);
};

export const isStateControlledMedication = (usState, medicationName) => {
  return (controlledDrugsByState[medicationName] || []).includes(usState);
};

export const nullUUID = "00000000-0000-0000-0000-000000000000";

export const isNullUUID = (uuid) => {
  return uuid === nullUUID;
};

export const isNullDate = (date) => {
  return (
    date === null ||
    date === undefined ||
    date === "" ||
    date === "0001-01-01T00:00:00Z"
  );
};

export const addressValidationSchema = Yup.object().shape({
  address1: Yup.string().required("Required"),
  city: Yup.string().required("Required"),
  usState: Yup.string().required("Required"),
  zip: Yup.string().required("Required"),
});

export const getConsultationTypeByProduct = (product) => {
  switch (product) {
    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_VISIT_ONLY:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
      return CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY;
    case ORDER_TYPE_PROP_1_PACK_REFILL:
    case ORDER_TYPE_PROP_2_PACK_REFILL:
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
      return CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP;
  }
};

export function anyOrderItemsAreMailOrder(orderItems) {
  return orderItems.some(
    (orderItem) => !isPharmacyPickupOrderType(orderItem.productSlug)
  );
}

export function anyOrderItemsArePharmacyPickup(orderItems) {
  return orderItems.some((orderItem) =>
    isPharmacyPickupOrderType(orderItem.productSlug)
  );
}

export function getAllMedicationsFromVisits(visits) {
  const medications = [];
  visits.forEach((visit) => {
    (visit.visitPrescriptions || []).forEach(
      ({ medicationName, medicationDosage }) => {
        medications.push({
          medicationName,
          medicationDosage,
        });
      }
    );
  });
  return medications;
}

/*
  Get medications to check if patient has ever been prescribed 20mg propranolol or atenolol.
  If doctor is prescribing 10mg propranolol and patient has received 20mg prop or atenolol in the past,
  then show a confirm.
*/
export function verifyNoteWithPrevMeds(note, meds) {
  const has20mgPropInPast = meds.some(
    ({ medicationName, medicationDosage }) =>
      medicationDosage === "20mg" &&
      medicationName.toLowerCase() === "propranolol"
  );
  const hasAtenololInPast = meds.some(
    ({ medicationName }) => medicationName.toLowerCase() === "atenolol"
  );

  if (
    note.includes("propranolol") &&
    !note.includes("20mg") &&
    has20mgPropInPast
  ) {
    if (
      !confirm(
        "CAUTION: Patient has been prescribed Propranolol 20mg in the past. Continue with 10mg prescription?"
      )
    ) {
      return false;
    }
  } else if (note.includes("propranolol") && hasAtenololInPast) {
    if (
      !confirm(
        "CAUTION: Patient has been prescribed Atenolol 25mg in the past. Continue with Propranolol prescription?"
      )
    ) {
      return false;
    }
  }

  return true;
}

export function getOptionsForEditOrderType(orderType) {
  // Handle empty orderType - default to sleep membership options for insomnia visits
  if (!orderType || orderType === "") {
    return [
      ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY,
      ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_QUARTERLY,
      ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY,
      ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY,
    ];
  }

  // Sleep
  if (isSleepOrderType(orderType)) {
    if (orderType.includes("follow-up")) {
      return [
        ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_FOLLOW_UP,
        ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_FOLLOW_UP,
      ];
    } else {
      return [
        ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY,
        ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_QUARTERLY,
        ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY,
        ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY,
      ];
    }
  }

  // Flavored BBs - now includes cross-category switching
  if (isFlavoredBBOrderType(orderType)) {
    if (orderType.includes("follow-up")) {
      return [
        ORDER_TYPE_PROP_1_PACK_FOLLOW_UP,
        ORDER_TYPE_PROP_2_PACK_FOLLOW_UP,
        ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP,
        ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP,
        // Removed ate-28 order types as they're now part of flavored PA
        ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP,
        ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP,
      ];
    } else {
      return [
        ORDER_TYPE_PROP_1_PACK,
        ORDER_TYPE_PROP_2_PACK,
        ORDER_TYPE_PROP_1_PACK_FLAVORED,
        ORDER_TYPE_PROP_2_PACK_FLAVORED,
        // Removed ate-28 order types as they're now part of flavored PA
        ORDER_TYPE_ATE_SCO_1_PACK,
        ORDER_TYPE_ATE_SCO_2_PACK,
      ];
    }
  }

  // BBs - now includes cross-category switching to Atenolol compounded
  if (isBBOrderType(orderType)) {
    if (orderType.includes("follow-up")) {
      return [
        ORDER_TYPE_PROP_1_PACK_FOLLOW_UP,
        ORDER_TYPE_PROP_2_PACK_FOLLOW_UP,
        ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP,
        ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP,
        ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP,
        ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP,
      ];
    } else {
      return [
        ORDER_TYPE_PROP_1_PACK,
        ORDER_TYPE_PROP_2_PACK,
        ORDER_TYPE_PROP_1_PACK_FLAVORED,
        ORDER_TYPE_PROP_2_PACK_FLAVORED,
        ORDER_TYPE_ATE_SCO_1_PACK,
        ORDER_TYPE_ATE_SCO_2_PACK,
      ];
    }
  }

  // Scopolamine Addon
  if (isScopolamineAddonOrderType(orderType)) {
    return [
      ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_MAIL_ORDER,
      ORDER_TYPE_SCOPOLAMINE_ADDON_10_PACK_MAIL_ORDER,
    ];
  }

  // Atenolol Compounded - now includes cross-category switching to all PA order types
  if (isAteScoCompoundedOrderType(orderType)) {
    if (orderType.includes("follow-up")) {
      return [
        ORDER_TYPE_PROP_1_PACK_FOLLOW_UP,
        ORDER_TYPE_PROP_2_PACK_FOLLOW_UP,
        ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP,
        ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP,
        // Only combo order types remain for atenolol
        ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP,
        ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP,
      ];
    } else {
      return [
        ORDER_TYPE_PROP_1_PACK,
        ORDER_TYPE_PROP_2_PACK,
        ORDER_TYPE_PROP_1_PACK_FLAVORED,
        ORDER_TYPE_PROP_2_PACK_FLAVORED,
        // Only combo order types remain for atenolol
        ORDER_TYPE_ATE_SCO_1_PACK,
        ORDER_TYPE_ATE_SCO_2_PACK,
      ];
    }
  }

  // Return null instead of undefined for unknown order types
  return null;
}

export function isFollowUpConsultationType(consultationType) {
  return (
    consultationType ===
      CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP ||
    consultationType === CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP
  );
}

export function isFollowUpVisitType(visitType) {
  const followUpVisitTypes = [
    VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP,
    VISIT_TYPE_INSOMNIA_FOLLOW_UP,
    VISIT_TYPE_GENERALIZED_ANXIETY_FOLLOW_UP,
  ];
  return followUpVisitTypes.includes(visitType);
}

export function isFollowUpOrderType(orderType) {
  return orderType.includes("follow-up");
}

export function displayOrderType(visitOrderType) {
  switch (visitOrderType) {
    case ORDER_TYPE_PROP_VISIT_ONLY:
      return "Performance Anxiety - Pickup";
    case ORDER_TYPE_PROP_1_PACK:
      return "Performance Anxiety - 1 Pack";
    case ORDER_TYPE_PROP_2_PACK:
      return "Performance Anxiety - 2 Pack";
    case ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP:
      return "Performance Anxiety - Pickup (Follow Up)";
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
      return "Performance Anxiety - 1 Pack (Follow Up)";
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
      return "Performance Anxiety - 2 Pack (Follow Up)";

    // Flavored
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
      return "Performance Anxiety Flavored 🍒 - 1 Pack";
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
      return "Performance Anxiety Flavored 🍒 - 2 Pack";
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
      return "Performance Anxiety Flavored 🍒 - 1 Pack (Follow Up)";
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
      return "Performance Anxiety Flavored 🍒 - 2 Pack (Follow Up)";
    case ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED:
      return "Performance Anxiety Flavored 🍒 - Sample Pack";
    case ORDER_TYPE_PROP_SAMPLE_PACK_ALL_FLAVORS:
      return "Performance Anxiety Flavored 🍒 - All Flavors Sample Pack";

    // Sleep
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY:
      return "Insomnia - Monthly";
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_QUARTERLY:
      return "Insomnia - 3 Month Quarterly";
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_SEMIANNUAL:
      return "Insomnia - 6 Month Semi-Annual";
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_FOLLOW_UP:
      return "Insomnia - Follow Up";

    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY:
      return "Insomnia Pickup - Monthly";
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY:
      return "Insomnia Pickup - 3 Month Quarterly";
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_SEMIANNUAL:
      return "Insomnia Pickup - 6 Month Semi-Annual";

    case ORDER_TYPE_SLEEP_RX_TRIAL_VISIT_ONLY_ONE_MONTH:
      return "Insomnia Pickup - 1 Month Trial";
    case ORDER_TYPE_SLEEP_RX_TRIAL_VISIT_ONLY_TWO_MONTHS:
      return "Insomnia Pickup - 2 Month Trial";
    case ORDER_TYPE_SLEEP_RX_TRIAL_MAIL_ORDER_ONE_MONTH:
      return "Insomnia - 1 Month Trial";
    case ORDER_TYPE_SLEEP_RX_TRIAL_MAIL_ORDER_TWO_MONTHS:
      return "Insomnia - 2 Month Trial";

    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_FOLLOW_UP:
      return "Insomnia Pickup - Follow-Up";

    // Scopolamine Addon
    case ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_MAIL_ORDER:
      return "Scopolamine Addon - 4 Pack (Mail Order)";
    case ORDER_TYPE_SCOPOLAMINE_ADDON_10_PACK_MAIL_ORDER:
      return "Scopolamine Addon - 10 Pack (Mail Order)";
    case ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_VISIT_ONLY:
      return "Scopolamine Addon - 4 Pack (Local Pickup)";

    case ORDER_TYPE_ATE_SCO_1_PACK:
      return "Atenolol 28mg + Scopolamine 0.25mg - 1 Pack";
    case ORDER_TYPE_ATE_SCO_1_PACK_REFILL:
      return "Atenolol 28mg + Scopolamine 0.25mg - 1 Pack (Refill)";
    case ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP:
      return "Atenolol 28mg + Scopolamine 0.25mg - 1 Pack (Follow Up)";
    case ORDER_TYPE_ATE_SCO_2_PACK:
      return "Atenolol 28mg + Scopolamine 0.25mg - 2 Pack";
    case ORDER_TYPE_ATE_SCO_2_PACK_REFILL:
      return "Atenolol 28mg + Scopolamine 0.25mg - 2 Pack (Refill)";
    case ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP:
      return "Atenolol 28mg + Scopolamine 0.25mg - 2 Pack (Follow Up)";

    default:
      return `{UNKNOWN VISIT ORDER TYPE: ${visitOrderType}}`;
  }
}

export function displayOrderTypePacks(orderType, subcategory) {
  switch (orderType) {
    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
      return "Beta Blockers 1 Pack";
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
      return "Beta Blockers 2 Pack";
    case ORDER_TYPE_PROP_VISIT_ONLY:
    case ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP:
      return "Beta Blockers Pickup (Visit Only)";
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
      return `Beta Blockers Flavored 1 Pack ${
        getFlavorName(subcategory) && `(${getFlavorName(subcategory)})`
      }`.trim();
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
      return `Beta Blockers Flavored 2 Pack ${
        getFlavorName(subcategory) && `(${getFlavorName(subcategory)})`
      }`.trim();
    case ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED:
      return `Beta Blockers Flavored Sample Pack ${
        getFlavorName(subcategory) && `(${getFlavorName(subcategory)})`
      }`.trim();
    case ORDER_TYPE_PROP_SAMPLE_PACK_ALL_FLAVORS:
      return "Beta Blockers All Flavors Sample Pack";

    // Atenolol + Scopolamine Combo
    case ORDER_TYPE_ATE_SCO_1_PACK:
    case ORDER_TYPE_ATE_SCO_1_PACK_REFILL:
    case ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP:
      return `Atenolol + Scopolamine 1 Pack ${
        getFlavorName(subcategory) && `(${getFlavorName(subcategory)})`
      }`.trim();
    case ORDER_TYPE_ATE_SCO_2_PACK:
    case ORDER_TYPE_ATE_SCO_2_PACK_REFILL:
    case ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP:
      return `Atenolol + Scopolamine 2 Pack ${
        getFlavorName(subcategory) && `(${getFlavorName(subcategory)})`
      }`.trim();
  }
  return "";
}

export function generatePharmacyOrderLinkURL(pharmacyID, orderNumberOrFillID) {
  switch (pharmacyID) {
    case FILL_PHARMACY_ID_HONEYBEE_CA:
      return `https://partners.honeybeehealth.com/orders/${orderNumberOrFillID}`;
    default:
      return "";
  }
}

export function titleCase(str) {
  return str.replace("-", " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

const compoundedOrderTypes = [
  ORDER_TYPE_PROP_1_PACK_FLAVORED,
  ORDER_TYPE_PROP_2_PACK_FLAVORED,
  ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP,
  ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP,
  ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL,
  ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL,
  ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED,
  ORDER_TYPE_PROP_SAMPLE_PACK_ALL_FLAVORS,
  // Atenolol + Scopolamine combo order types
  ORDER_TYPE_ATE_SCO_1_PACK,
  ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP,
  ORDER_TYPE_ATE_SCO_1_PACK_REFILL,
  ORDER_TYPE_ATE_SCO_2_PACK,
  ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP,
  ORDER_TYPE_ATE_SCO_2_PACK_REFILL,
];

const compoundedPropranololOrderTypes = [
  ORDER_TYPE_PROP_1_PACK_FLAVORED,
  ORDER_TYPE_PROP_2_PACK_FLAVORED,
  ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP,
  ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP,
  ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL,
  ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL,
];

export function isCompoundedOrderType(orderType) {
  return compoundedOrderTypes.includes(orderType);
}

export function isCompoundedPropranololOrderType(orderType) {
  return compoundedPropranololOrderTypes.includes(orderType);
}

const scriptsureDrugNameMap = {
  PROPRANOLOL_10MG_FLAVORED: "Propranolol HCL 10mg",
  PROPRANOLOL_20MG_FLAVORED: "Propranolol HCL 20mg",
  PROPRANOLOL_12MG_FLAVORED: "Propranolol HCL 12mg",
  PROPRANOLOL_22MG_FLAVORED: "Propranolol HCL 22mg",
  ATENOLOL_28MG_FLAVORED: "Atenolol 28mg",
  DOXEPIN_3MG_GUMMY: "Doxepin 3mg",
  DOXEPIN_7MG_GUMMY: "Doxepin 7mg",
  ATENOLOL_28MG_SCOPOLAMINE_025MG_COMBO: "Atenolol 28mg + Scopolamine 0.25mg",
};

function getScriptsureDrugNamePrefix(productSlug) {
  if (productSlug.includes("1-pack")) {
    return "1 Pack";
  } else if (productSlug.includes("2-pack")) {
    return "2 Pack";
  } else if (productSlug.includes("trial")) {
    return "Sample Pack";
  }
  return "";
}

function createScriptsureDrugNameFromMedNameKey(medName) {
  const [medConstant, quantity] = medName.split("-");
  return `${scriptsureDrugNameMap[medConstant]} x${quantity}`;
}

export function getScriptsureDrugName(orderItem = {}, medName) {
  const { subcategory, productSlug = "" } = orderItem;

  if (!productSlug) {
    return createScriptsureDrugNameFromMedNameKey(medName);
  }

  // scriptsureDrugNamePrefix denotes the quantity (1 pack, 2 pack, or sample)
  const scriptsureDrugNamePrefix = getScriptsureDrugNamePrefix(productSlug);
  // scriptsureDrugName denotes the drug name and dosage
  const scriptsureDrugName = scriptsureDrugNameMap[medName];
  // subcategory denotes the flavor
  return `${scriptsureDrugNamePrefix} ${scriptsureDrugName} ${
    getFlavorName(subcategory) && `(${getFlavorName(subcategory)})`
  }`.trim();
}

export function getFlavorName(flavor = "") {
  switch (flavor) {
    case TANGERINE_FLAVOR:
      return FLAVOR_NAME_TANGERINE;
    case SPEARMINT_FLAVOR:
      return FLAVOR_NAME_SPEARMINT;
    case FRUIT_PUNCH_FLAVOR:
      return FLAVOR_NAME_FRUIT_PUNCH;
    case CHOCO_CHILL_MINT_FLAVOR:
      return FLAVOR_NAME_CHOCO_CHILL_MINT;
    case CITRUS_ZEN_FLAVOR:
      return FLAVOR_NAME_CITRUS_ZEN;
    default:
      return titleCase(flavor);
  }
}

export function isCompoundedMedication(medicationConstant) {
  return (
    medicationConstant.includes("FLAVORED") ||
    medicationConstant.endsWith("GUMMY")
  );
}

export function isQuarterlySubscription(orderType) {
  return orderType.includes("quarterly");
}
